import { useStateMachine } from "components/context/stateMachine/StateMachineContext"
import { Steps } from "components/csd-enrollment/workflow/steps"
import React, { useEffect, useRef, useState } from "react"
import L10n from "lib/carrot-l10n"
import { Body, BodySmall } from "components/views/atoms/Bodies"
import { Container, FlexContainer, H2, TextLink } from "@carrotfertility/carotene"
import { useProgressTracker } from "../../../views/modal/ProgressTracker"
import { BenefitEnrollmentFlowModalBackButton, BenefitEnrollmentModalContinueButton, DateOfBirth } from "../../shared"
import { Asterisk, Label } from "../../../views/atoms/Atoms"
import moment, { Moment } from "moment"
import useSubmitWithErrorCatch from "../../hooks/useSubmitWithErrorCatch"
import { useCurrentUser } from "../../../context/user/UserContext"
import { CSD_CONFIGURATION, useUserDeductible } from "components/context/user/DeductibleContext"
import { useBenefitEnrollment } from "components/context/enrollment/BenefitEnrollmentContext"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

interface UseDateOfBirthInputType {
  isDateOfBirthValid: () => boolean
  validDateOfBirth: boolean
  dateOfBirth: Moment | null
  dateOfBirthRef: any
}

function useDateOfBirthInput(defaultDateOfBirth: null | string): UseDateOfBirthInputType {
  const dateOfBirthRef = useRef(null)
  const [dateOfBirth, setDateOfBirth] = useState(null)
  const [validDateOfBirth, setValidDateOfBirth] = useState(true)

  function isDateOfBirthValid(): boolean {
    const dobInputIsValid = dateOfBirthRef.current.validate()
    setValidDateOfBirth(dobInputIsValid)
    return dobInputIsValid
  }

  useEffect(() => {
    if (defaultDateOfBirth != null) {
      setDateOfBirth(moment(defaultDateOfBirth))
    }
  }, [defaultDateOfBirth])

  return {
    isDateOfBirthValid,
    validDateOfBirth,
    dateOfBirth,
    dateOfBirthRef
  }
}

interface CoverageDateStepProps {
  title: string
  description: string
  stepName: string
  isMember?: boolean
  initialValue: string | null
}

function CoverageDateStep({
  title,
  description,
  stepName,
  isMember,
  initialValue
}: CoverageDateStepProps): JSX.Element {
  const { setProgress } = useProgressTracker()
  const { setNextStep, goBackTwoSteps } = useStateMachine(stepName, initialValue)
  const {
    memberCoverageStartDate,
    memberCoverageEndDate,
    partnerCoverageStartDate,
    partnerCoverageEndDate,
    updateUserInfo
  } = useCurrentUser()
  const {
    isDateOfBirthValid: isStartDateValid,
    validDateOfBirth: validStartDate,
    dateOfBirth: startDate,
    dateOfBirthRef: startDateRef
  } = useDateOfBirthInput(isMember ? memberCoverageStartDate : partnerCoverageStartDate)
  const {
    isDateOfBirthValid: isEndDateValid,
    validDateOfBirth: validEndDate,
    dateOfBirth: endDate,
    dateOfBirthRef: endDateRef
  } = useDateOfBirthInput(isMember ? memberCoverageEndDate : partnerCoverageEndDate)

  setProgress(1, "YOUR INSURANCE COVERAGE")

  const { onSubmit: memberOnSubmit } = useSubmitWithErrorCatch(async () => {
    if (!isStartDateValid() || !isEndDateValid) return
    const startDateVal = startDateRef.current.getMoment().format("YYYY-MM-DD")
    const endDateVal = endDateRef.current.getMoment().format("YYYY-MM-DD")

    updateUserInfo({
      memberCoverageStartDate: startDateVal,
      memberCoverageEndDate: endDateVal
    })
    setNextStep(stepName, "")
  })

  const { onSubmit: partnerOnSubmit } = useSubmitWithErrorCatch(async () => {
    if (!isStartDateValid() || !isEndDateValid) return
    const startDateVal = startDateRef.current.getMoment().format("YYYY-MM-DD")
    const endDateVal = endDateRef.current.getMoment().format("YYYY-MM-DD")

    updateUserInfo({
      partnerCoverageStartDate: startDateVal,
      partnerCoverageEndDate: endDateVal
    })
    setNextStep(stepName, "")
  })

  return (
    <div>
      <Container padding="none" stack="huge">
        <BenefitEnrollmentFlowModalBackButton handleGoBack={goBackTwoSteps} />
      </Container>
      <H2 stack="tiny" variant="dark">
        {title}
      </H2>
      <Container padding="none" stack="huge">
        <Body color="black-80">{description}</Body>
      </Container>

      <Label htmlFor="dateOfBirth" valid>
        Start Date
        <Asterisk />
      </Label>
      <Container padding="none" stack="giant">
        <DateOfBirth
          showLabel={false}
          dateOfBirthRef={startDateRef}
          dateOfBirth={startDate}
          validDateOfBirth={validStartDate}
        />
      </Container>

      <Label htmlFor="dateOfBirth" valid>
        End Date
        <Asterisk />
      </Label>
      <Container padding="none" stack="giant">
        <DateOfBirth
          showLabel={false}
          dateOfBirthRef={endDateRef}
          dateOfBirth={endDate}
          validDateOfBirth={validEndDate}
        />
      </Container>
      <FlexContainer padding="none" justifyContent="space-between">
        <div>
          <TextLink onClick={(): void => setNextStep(stepName, "IM_NOT_SURE")}>
            <BodySmall color="black-80">{l`I'm not sure`}</BodySmall>
          </TextLink>
        </div>
        <BenefitEnrollmentModalContinueButton onClick={isMember ? memberOnSubmit : partnerOnSubmit} />
      </FlexContainer>
    </div>
  )
}

export function MemberPlanCoverageDateStep(): JSX.Element {
  const { csdConfiguration } = useUserDeductible()
  const { shouldSkipMemberCoverageDate } = useBenefitEnrollment()
  const collectDeductiblePeriod = [
    CSD_CONFIGURATION.MEMBER_AND_PARTNER_SEPARATE,
    CSD_CONFIGURATION.MEMBER_AND_PARTNER_COMBINED,
    CSD_CONFIGURATION.MEMBER_ONLY
  ].includes(csdConfiguration)
  const textProps = collectDeductiblePeriod
    ? {
        title: l`When does the annual deductible period on your medical plan begin and end?`,
        description: l`This often aligns with coverage start and end dates, which may be listed on your insurance card. 
    The accuracy of your answer may have financial implications, so if you're not sure, please check with the 
    relevant insurance provider or HR department.`
      }
    : {
        title: l`When does your medical plan coverage start and end?`,
        description: l`These dates may be listed on your insurance card. The accuracy of your answer may have financial implications, so if you're not sure, please check with the relevant insurance provider or HR department.`
      }
  return (
    <CoverageDateStep
      {...textProps}
      initialValue={shouldSkipMemberCoverageDate ? "" : null}
      stepName={Steps.MEMBER_DEDUCTIBLE_PERIOD_COVERAGE_DATE}
      isMember
    />
  )
}

export function PartnerPlanCoverageDateStep(): JSX.Element {
  const { csdConfiguration } = useUserDeductible()
  const { shouldSkipPartnerCoverageDate } = useBenefitEnrollment()

  const collectDeductiblePeriod = [
    CSD_CONFIGURATION.MEMBER_AND_PARTNER_SEPARATE,
    CSD_CONFIGURATION.PARTNER_ONLY
  ].includes(csdConfiguration)
  const textProps = collectDeductiblePeriod
    ? {
        title: l`When does the annual deductible period on your partner's medical plan start and end?`,
        description: l`This often aligns with coverage start and end dates, which may be listed on your insurance card. 
        The accuracy of your answer may have financial implications, so if you're not sure, please check with the 
        relevant insurance provider or HR department.`
      }
    : {
        title: l`When does your partner's medical plan coverage start and end?`,
        description: l`These dates may be listed on your insurance card. The accuracy of your answer may have financial implications, so if you're not sure, please check with the relevant insurance provider or HR department.`
      }
  return (
    <CoverageDateStep
      {...textProps}
      initialValue={shouldSkipPartnerCoverageDate ? "" : null}
      stepName={Steps.PARTNER_DEDUCTIBLE_PERIOD_COVERAGE_DATE}
    />
  )
}
